import { useEffect, useMemo, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import { Redirect, Route, Switch, generatePath, useLocation, useParams } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import AICompanionTabs from '../features/interactiveEducation/components/AICompanionTabs';
import CompanionTabWrapper from '../features/interactiveEducation/components/CompanionTabWrapper';
import SummarizeTab from '../features/interactiveEducation/components/SummarizeTab';
import ApiError from '../components/Errors/ApiError';
import LearningPageMainSection from '../containers/TrackPage/LearningPageMainSection';
import UsageTracker from '../utils/UsageTracker';
import { LEGACY_SHORT_CONTENT_URL_FRAGMENT, PAGE_NAMES, SHORT_CONTENT_URL_FRAGMENT } from '../constants/Navigation';
import { SCREENS } from '../constants/Tracking';
import { isQuizMeReleaseShortId } from '../features/interactiveEducation/utils/quizMeUtils';
import { LearningContentPageLayout } from '../features/learningContent';
import { NextPlaylistContentOverview, PlaylistComplete, PlaylistIncompleteItems } from '../features/playlistContent';
import { RecommendationsForLearningContent, useSetLastClickedMLRecommendationItemFromUrl } from '../features/recommendations';
import { NamedRoute } from '../features/routing';
import { ShortContentDetails, ShortContentHeader, ShortContentInstructors, ShortContentPlaylistNavigation, ShortContentVideo, ShortsSurvey, useShort, useShortFromPropsOrParams } from '../features/shortContent';
import { useShortProgressById } from '../features/userProgress/userProgressQueries';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ShortContentBody = ({
  shortId,
  language
}) => {
  const [displayAICompanion, setDisplayAICompanion] = useState(false);
  const [displaySummary, setDisplaySummary] = useState(false);
  const {
    isNotLaptop,
    isTablet
  } = useMediaQuery();
  const {
    short
  } = useShort(shortId);
  const {
    playlistId
  } = useShortFromPropsOrParams();
  const isQuizMeShort = isQuizMeReleaseShortId(shortId);
  const professorId = useMemo(() => short === null || short === void 0 ? void 0 : short.professors.map(professor => professor.id), [short]);
  const interactiveContentTrackingProps = useMemo(() => ({
    id: shortId,
    businessObjectives: short === null || short === void 0 ? void 0 : short.businessObjectives,
    creatorHubId: short === null || short === void 0 ? void 0 : short.hubId,
    hsSoftwareFilters: short === null || short === void 0 ? void 0 : short.hubSpotSoftwareTools,
    contentLanguage: short === null || short === void 0 ? void 0 : short.language,
    contentType: 'SHORT_FORM_CONTENT',
    professorId,
    subCategory: short === null || short === void 0 ? void 0 : short.subCategories,
    useCases: short === null || short === void 0 ? void 0 : short.useCases
  }), [shortId, short, professorId]);
  const handleAICompanionDisplay = flag => {
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({
      action: 'ai-quiz-me-button-clicked'
    }, interactiveContentTrackingProps));
    setDisplayAICompanion(flag);
    setDisplaySummary(false);
  };
  const handleSummarizeDisplay = flag => {
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({
      action: 'ai-summarize-button-clicked'
    }, interactiveContentTrackingProps));
    setDisplaySummary(flag);
    setDisplayAICompanion(false);
  };
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "SHORT_CONTENT_LOADED",
    children: [/*#__PURE__*/_jsx(ShortsSurvey, {}), /*#__PURE__*/_jsxs(Flex, {
      alignItems: "stretch",
      flexDirection: "column",
      gap: "900",
      children: [/*#__PURE__*/_jsxs(LearningPageMainSection, {
        children: [/*#__PURE__*/_jsx(ShortContentVideo, {
          displayAICompanion: displayAICompanion,
          displaySummary: displaySummary,
          handleAICompanionDisplay: handleAICompanionDisplay,
          handleSummarizeDisplay: handleSummarizeDisplay
        }), isNotLaptop && isQuizMeShort && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(AccordionTransition, {
            open: displayAICompanion,
            children: /*#__PURE__*/_jsx(AICompanionTabs, {
              additionalTracking: interactiveContentTrackingProps,
              contentId: shortId,
              contentType: "SHORT_FORM_CONTENT"
            })
          }), /*#__PURE__*/_jsx(AccordionTransition, {
            open: displaySummary,
            children: /*#__PURE__*/_jsx(View, {
              paddingBlockStart: "400",
              children: /*#__PURE__*/_jsx(CompanionTabWrapper, {
                children: /*#__PURE__*/_jsx(SummarizeTab, {
                  additionalTracking: interactiveContentTrackingProps,
                  contentId: shortId,
                  contentType: "SHORT_FORM_CONTENT"
                })
              })
            })
          })]
        })]
      }), /*#__PURE__*/_jsx(LearningPageMainSection, {
        children: /*#__PURE__*/_jsx(Flex, {
          paddingInline: isTablet || playlistId ? '400' : undefined,
          children: /*#__PURE__*/_jsx(ShortContentDetails, {})
        })
      }), /*#__PURE__*/_jsx(LearningPageMainSection, {
        children: /*#__PURE__*/_jsx(View, {
          paddingInline: isTablet || playlistId ? '400' : undefined,
          children: /*#__PURE__*/_jsx(ShortContentInstructors, {})
        })
      }), /*#__PURE__*/_jsx(LearningPageMainSection, {
        children: /*#__PURE__*/_jsx(View, {
          paddingInline: isTablet || playlistId ? '400' : undefined,
          children: /*#__PURE__*/_jsx(RecommendationsForLearningContent, {
            appearsIn: "short-content-recommendations",
            description: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.shortContent.videoRecommendations.description"
            }),
            displayFullCardsOnMobile: true,
            errorMarker: "RECOMMENDATIONS_FOR_ENTITY_API_ERROR",
            id: shortId,
            language: language,
            navMarker: "RECOMMENDATIONS_FOR_ENTITY_LOADED",
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.shortContent.videoRecommendations.title"
            }),
            type: "SHORT_FORM_CONTENT"
          })
        })
      })]
    })]
  });
};
export const ShortPlaylistCompletionView = ({
  playlistNavigation,
  shortId
}) => {
  switch (playlistNavigation) {
    case 'next-in-playlist':
      return /*#__PURE__*/_jsx(NextPlaylistContentOverview, {
        shortId: shortId
      });
    case 'playlist-incomplete':
      return /*#__PURE__*/_jsx(PlaylistIncompleteItems, {});
    default:
      return /*#__PURE__*/_jsx(PlaylistComplete, {});
  }
};
export const ShortContent = () => {
  const {
    shortError,
    shortLoading,
    shortId,
    playlistId,
    playlistNavigation,
    short
  } = useShortFromPropsOrParams();
  useSetLastClickedMLRecommendationItemFromUrl({
    id: shortId ? Number(shortId) : undefined,
    type: 'SHORT_FORM_CONTENT'
  });
  const {
    shortStatus
  } = useShortProgressById(shortId);
  useEffect(() => {
    if (short) {
      UsageTracker.track('pageView', {
        screen: SCREENS.SHORT,
        contentId: shortId,
        contentName: short.name
      });
      if (window.scrollTo) {
        window.scrollTo(0, 0);
      }
    }
  }, [short, shortId]);
  if (!shortId) return null;
  if (shortLoading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true,
    minHeight: 200
  });
  if (shortError) {
    return /*#__PURE__*/_jsx(ApiError, {
      error: shortError,
      marker404: "SHORT_CONTENT_API_404",
      markerDefault: "SHORT_CONTENT_API_ERROR"
    });
  }
  return /*#__PURE__*/_jsx(LearningContentPageLayout, {
    header: /*#__PURE__*/_jsx(ShortContentHeader, {}),
    navigation: playlistId && short && /*#__PURE__*/_jsx(ShortContentPlaylistNavigation, {
      duration: short.duration,
      name: short.name,
      shortId: shortId,
      status: shortStatus || ''
    }),
    children: playlistNavigation ? /*#__PURE__*/_jsx(ShortPlaylistCompletionView, {
      playlistNavigation: playlistNavigation,
      shortId: shortId
    }) : short && /*#__PURE__*/_jsx(ShortContentBody, {
      language: short === null || short === void 0 ? void 0 : short.language,
      playlistId: playlistId,
      shortId: shortId,
      useCases: short.useCases
    })
  });
};
const RedirectToLanguagelessShort = () => {
  const location = useLocation();
  const {
    shortId,
    playlistNavigation
  } = useParams();
  let newPath = generatePath(SHORT_CONTENT_URL_FRAGMENT, {
    shortId
  });
  if (playlistNavigation) newPath += `/${playlistNavigation}`;
  return /*#__PURE__*/_jsx(Redirect, {
    to: Object.assign({}, location, {
      pathname: newPath
    })
  });
};
export const ShortContentRoute = () => {
  return /*#__PURE__*/_jsxs(Switch, {
    children: [/*#__PURE__*/_jsx(NamedRoute, {
      component: ShortContent,
      exact: true,
      name: PAGE_NAMES.SHORT_CONTENT,
      path: SHORT_CONTENT_URL_FRAGMENT
    }), /*#__PURE__*/_jsx(Route, {
      component: RedirectToLanguagelessShort,
      exact: true,
      path: LEGACY_SHORT_CONTENT_URL_FRAGMENT
    })]
  });
};
export default ShortContentRoute;